// apps/client/src/ui/screens/invite.tsx (M0-T17) — /invite/<code> landing: preview the town, pick a free plot (or learn a villager
// moves out of a full town), move in, then enter. Shown by phaseScreen in phase `towns`.
import { tuning } from '@whiskerton/content';
import type { ApiErrorCode, InviteCode, InvitePreviewResponse, TownId } from '@whiskerton/shared';
import { generateTown } from '@whiskerton/shared/map/generate';   // subpath-only (not in the barrel)
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/endpoints';
import { HttpError } from '../../api/http';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel } from '../kit';
import { Minimap } from '../minimap';
import { defaultPlot, pickablePlots } from '../plotPick';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { invite: { code: InviteCode } } }

const MAP_ASPECT = tuning.map.w / tuning.map.h;

/** Leave /invite/<code> for /, staying in phase `towns`. closeAll() hands App a new stack, so it re-reads the path. */
function backToTowns(): void {
  try { history.replaceState(null, '', '/'); } catch { /* sandboxed history */ }
  const s = useGame.getState();
  s.closeAll();
  if (s.phase !== 'towns') s.setPhase('towns');
}
async function enter(townId: TownId): Promise<void> {
  try { history.replaceState(null, '', '/'); } catch { /* sandboxed history */ }
  const s = useGame.getState();
  try { s.setMe(await api.me()); } catch { /* the list refreshes next boot; entering does not need it */ }
  useGame.getState().requestEnterTown(townId);
}
const codeOf = (e: unknown): ApiErrorCode => (e instanceof HttpError ? e.error : 'server_error');

function Invite({ params }: { params: { code: InviteCode } }) {
  const t = useT();
  const code = params.code;
  const [preview, setPreview] = useState<InvitePreviewResponse | null>(null);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [picked, setPicked] = useState<{ preview: InvitePreviewResponse; plot: number } | null>(null);   // the player's own pick
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { setPreview(await api.previewInvite(code)); }
    catch (e) { setError(codeOf(e)); }
  }, [code]);
  useEffect(() => { void load(); }, [load]);

  const town = useMemo(() => (preview ? generateTown(preview.town.seed, { genVersion: preview.town.genVersion }) : null),
    [preview]);

  const join = async () => {
    if (!preview) return;
    const req = { plot: preview.full ? null : plot };
    setBusy(true);
    try {
      const res = await api.acceptInvite(code, req);
      await enter(res.town.id);
    } catch (e) {
      const c = codeOf(e);
      useGame.getState().toast(`errors.${c}`, undefined, 'warn');
      if (c === 'plot_taken' || c === 'town_full' || c === 'plot_invalid') void load();   // someone moved in first: refresh the plots
    } finally { setBusy(false); }
  };

  const wrap = (title: string, body: React.ReactNode) => (
    <div className="layer-full hit" style={{ display: 'grid', placeItems: 'center' }}>
      <Panel title={title}><div style={{ display: 'grid', gap: 14, width: 760, maxWidth: '100%' }}>{body}</div></Panel>
    </div>
  );

  if (error) {
    return wrap(t('towns.title'), <>
      <p role="alert" style={{ margin: 0 }}>{t(`errors.${error}`)}</p>
      <Button onClick={backToTowns}>{t('towns.back')}</Button>
    </>);
  }
  if (!preview) return wrap(t('towns.title'), <p style={{ margin: 0 }}>{t('common.loading')}</p>);

  const p = preview;
  // The free plot nearest the town square is picked for you, so Move in works without touching the map (production bug: plots too
  // small to tap); tapping the map changes it. A fresh preview (after someone else moved in first) starts again from its own default.
  const picking = !p.full && !p.alreadyMember && !p.cannotJoin;
  const plot = !picking || !town ? null
    : picked?.preview === p ? picked.plot : defaultPlot(town.map, pickablePlots(town.map.plots, p.takenPlots, p.freePlots));
  const canJoin = !busy && !p.cannotJoin && !p.alreadyMember && (p.full || plot !== null);
  const taken = [...p.takenPlots].sort((a, b) => a.plot - b.plot);
  // Two columns on a landscape phone (§8.7): the map on the left, sized to the panel's height; who lives where, Move in and Back on the
  // right, with the actions pinned to the column's foot and the list scrolling above them if a full town makes it long. The game is
  // landscape-only (§3, §8.7), so the two columns stay side by side at every size. Offsets from 86cqh (the panel's max height, .panel):
  // 52px is the panel's own padding (30 top + 22 bottom); the map's 82px adds the "Pick your plot" label and its gap (~30).
  return wrap(t('towns.inviteTitle', { town: p.town.name }), (
    <div style={{ display: 'grid', gap: 22, gridTemplateColumns: 'minmax(0, 1.2fr) minmax(190px, 1fr)', alignItems: 'start' }}>
      <div style={{ display: 'grid', gap: 8 }}>
        {picking && <strong>{t('towns.pickYourPlot')}</strong>}
        <div style={{ width: `min(100%, calc((86cqh - 82px) * ${MAP_ASPECT}))` }}>
          {town ? <Minimap town={town} selected={plot} free={p.freePlots} taken={p.takenPlots} onPick={picking ? (n) => setPicked({ preview: p, plot: n }) : undefined} />
            : <p style={{ margin: 0 }}>{t('common.loading')}</p>}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 'calc(86cqh - 52px)', minHeight: 0 }}>
        {/* M2 playtest (shot 46): beside the full-town notice the list alone scrolled, in a box a row and a half tall. The notice and
            the list now scroll together in all the height above the actions. */}
        <div data-testid="invite-who" style={{ display: 'grid', gap: 12, alignContent: 'start', overflow: 'auto', minHeight: 0, flex: '0 1 auto' }}>
          <p style={{ margin: 0 }}>{t('towns.mayorOf', { name: p.town.mayorName })}</p>
          {p.alreadyMember ? <p style={{ margin: 0 }}>{t('towns.alreadyMember')}</p>
            : p.cannotJoin ? <p role="alert" style={{ margin: 0 }}>{t('towns.cannotJoin')}</p>
            : p.full ? <p style={{ margin: 0 }}>{t('towns.villagerMovesOut')}</p> : null}
          {taken.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6, alignContent: 'start' }}>
              {taken.map((x) => (
                <li key={x.plot} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span aria-hidden="true" className="num" style={{ display: 'inline-grid', placeItems: 'center', minWidth: 26, height: 22, borderRadius: 6,
                    background: 'color-mix(in srgb, var(--wood-600) 55%, transparent)', color: '#fff', fontWeight: 900, fontSize: 13 }}>{x.plot + 1}</span>
                  <span>{t('towns.plotTaken', { name: x.name })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ display: 'grid', gap: 10, marginTop: 'auto', flex: 'none' }}>
          {picking && town && (
            <p data-testid="invite-status" aria-live="polite" style={{ margin: 0, fontSize: 13, lineHeight: 1.25 }}>
              {plot === null ? t('towns.needPlot') : t('towns.plotPicked', { n: plot + 1 })}
            </p>
          )}
          {p.alreadyMember ? <Button onClick={() => void enter(p.town.id)}>{t('towns.enter')}</Button>
            : !p.cannotJoin && <Button disabled={!canJoin} onClick={() => void join()}>{busy ? t('towns.joining') : t('towns.join')}</Button>}
          <Button variant="quiet" disabled={busy} onClick={backToTowns}>{t('towns.back')}</Button>
        </div>
      </div>
    </div>
  ));
}
export const screen: ScreenDef<'invite'> = { id: 'invite', layer: 'full', component: Invite };
